import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import Avatar from "../Avatar";
import PasswordInput from "./PasswordInput";
import { resetPassword } from "@/lib/api";

// FastAPI 422 detail can be array/object/string — map to a plain string.
function formatApiErrorDetail(detail) {
    if (detail == null) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Reset-password screen. Reached via the link in the password-reset email
// (URL pattern: /reset-password?token=...). On submit, the backend
// updates the password AND signs the user in — so we hand off to onAuthed
// (same callback the sign-in screen uses) to drop them into the dashboard.
export default function ResetPasswordScreen({ token, onAuthed, onBack }) {
    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [missingToken, setMissingToken] = useState(false);

    useEffect(() => {
        if (!token) setMissingToken(true);
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setError("");
        if (newPassword.length < 6) {
            setError("password needs to be at least 6 characters 🍵");
            return;
        }
        if (newPassword !== confirm) {
            setError("passwords don't match — give it another shot?");
            return;
        }
        setBusy(true);
        try {
            const data = await resetPassword(token, newPassword);
            onAuthed?.(data);
        } catch (err) {
            setError(
                formatApiErrorDetail(err.response?.data?.detail)
                    || "couldn't reset the password — link may have expired",
            );
            setBusy(false);
        }
    };

    const inputCls = "w-full bg-cream rounded-2xl px-4 py-3 font-body text-sm text-warm-900 outline-none border-2 border-transparent focus:border-terracotta transition-all";

    return (
        <div
            className="App font-body min-h-screen [min-height:100dvh] flex items-center justify-center px-5 py-10"
            data-testid="reset-password-screen"
        >
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-sm"
            >
                <div className="flex flex-col items-center mb-5">
                    <Avatar mood="happy" size={120} />
                    <h1 className="font-display text-3xl text-warm-900 mt-2">pick a new password 🍡</h1>
                </div>

                <div className="bg-surface rounded-clay shadow-clay p-5">
                    {missingToken ? (
                        <div className="space-y-3">
                            <p className="font-body text-sm text-warm-800">
                                this link looks incomplete — request a fresh reset email and try again 🌸
                            </p>
                            <button
                                type="button"
                                onClick={onBack}
                                className="w-full rounded-full bg-terracotta text-cream font-display text-sm py-3"
                                data-testid="reset-password-back-btn"
                            >
                                back to sign in
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form">
                            <p className="font-body text-sm text-warm-800 leading-relaxed">
                                you&apos;re almost back in. pick something you&apos;ll remember.
                            </p>
                            <PasswordInput
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="new password (6+ chars)"
                                minLength={6}
                                autoComplete="new-password"
                                className={inputCls}
                                testId="reset-password-new-input"
                            />
                            <PasswordInput
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                placeholder="type it again"
                                minLength={6}
                                autoComplete="new-password"
                                className={inputCls}
                                testId="reset-password-confirm-input"
                            />
                            {error && (
                                <p className="font-body text-xs text-terracotta" data-testid="reset-password-error">
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={busy}
                                className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-cream font-display text-base py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98]"
                                data-testid="reset-password-submit-btn"
                            >
                                <CheckCircle2 size={16} /> {busy ? "resetting…" : "save & sign in"}
                            </button>
                            <button
                                type="button"
                                onClick={onBack}
                                className="w-full font-body text-xs text-warm-500 hover:text-warm-900 transition-colors"
                                data-testid="reset-password-cancel-btn"
                            >
                                cancel — back to sign in
                            </button>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
